/**
 * HostingDashboard.tsx
 *
 * Customer-facing hosting overview, Glass + Aurora language.
 *   1. KPI row — sites, active count, fleet disk usage, SSL coverage
 *   2. Site cards — domain, status, disk meter, spec chips (CPU/RAM/BW/PHP),
 *      SSL badge, manage + visit actions
 *
 * Icons: Lucide via react-icons/lu (consistent stroke style with the rest
 * of the dashboard's inline SVGs).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Lu from 'react-icons/lu';
import { useAuth } from '../../contexts/AuthContext';
import DashShell from '../dash/DashShell';
import GlassCard from '../dash/GlassCard';
import Pill, { pillForStatus } from '../dash/Pill';
import DnsGuideModal from './DnsGuideModal';

// react-icons v5 types icons as `(props) => ReactNode`, which TS 4.9's JSX
// checker rejects (it wants ReactElement). Cast once at the boundary; the
// runtime components are ordinary SVG function components.
type IconCmp = React.FC<{ className?: string }>;
const LuGlobe = Lu.LuGlobe as unknown as IconCmp;
const LuCpu = Lu.LuCpu as unknown as IconCmp;
const LuMemoryStick = Lu.LuMemoryStick as unknown as IconCmp;
const LuHardDrive = Lu.LuHardDrive as unknown as IconCmp;
const LuArrowDownUp = Lu.LuArrowDownUp as unknown as IconCmp;
const LuLock = Lu.LuLock as unknown as IconCmp;
const LuServer = Lu.LuServer as unknown as IconCmp;
const LuExternalLink = Lu.LuExternalLink as unknown as IconCmp;
const LuPlus = Lu.LuPlus as unknown as IconCmp;
const LuShieldCheck = Lu.LuShieldCheck as unknown as IconCmp;
const LuFileCode2 = Lu.LuFileCode2 as unknown as IconCmp;

interface HostingAccountRow {
  id: string;
  primary_domain: string | null;
  hostname: string | null;
  ip_address: string | null;
  status: string;
  package_name: string | null;
  disk_quota_gb: number | null;
  ram_quota_mb: number | null;
  cpu_quota: number | null;
  bandwidth_quota_gb: number | null;
  current_disk_usage_mb: number | null;
  php_version: string | null;
  ssl_issued_at: string | null;
  dns_linked_at: string | null;
  created_at: string;
}

function diskPct(a: HostingAccountRow): number | null {
  if (!a.disk_quota_gb || a.current_disk_usage_mb == null) return null;
  return Math.min(100, (a.current_disk_usage_mb / (a.disk_quota_gb * 1024)) * 100);
}

function fmtGb(mb: number | null | undefined): string {
  if (mb == null) return '—';
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
}

const HostingDashboard: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [accounts, setAccounts] = useState<HostingAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dnsGuideFor, setDnsGuideFor] = useState<string | null>(null);
  const [verifyBusy, setVerifyBusy] = useState<string | null>(null);
  const [verifyMsg, setVerifyMsg] = useState<Record<string, string>>({});

  useEffect(() => {
    if (authLoading || !user) return;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const token = localStorage.getItem('token');
        const resp = await fetch(`${apiBase}/api/hosting/my-accounts`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const json = await resp.json();
        if (json.success) setAccounts(json.data || []);
        else setError(json.error || 'Failed to load hosting accounts');
      } catch (e: any) {
        setError(e.message || 'Network error');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [authLoading, user]);

  const stats = useMemo(() => {
    const active = accounts.filter(a => a.status === 'active').length;
    const usedMb = accounts.reduce((s, a) => s + (a.current_disk_usage_mb || 0), 0);
    const quotaGb = accounts.reduce((s, a) => s + (a.disk_quota_gb || 0), 0);
    const ssl = accounts.filter(a => a.ssl_issued_at).length;
    return { active, usedMb, quotaGb, ssl };
  }, [accounts]);

  if (authLoading) {
    return (
      <DashShell title="Web Hosting" subtitle="Loading…">
        <div className="h-40 rounded-2xl bg-white/60 backdrop-blur animate-pulse" />
      </DashShell>
    );
  }
  if (!user) {
    return (
      <DashShell title="Web Hosting" subtitle="Sign in required">
        <GlassCard delay={60} className="p-8 text-center">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Please log in</h2>
          <p className="text-slate-600">You need to be signed in to view your hosting accounts.</p>
        </GlassCard>
      </DashShell>
    );
  }

  return (
    <DashShell
      title="Web Hosting"
      subtitle="Your sites, resources and SSL at a glance."
      right={
        <Link
          to="/hosting"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition"
        >
          <LuPlus className="w-4 h-4" />
          New hosting plan
        </Link>
      }
    >
      {error && (
        <GlassCard delay={40} tint="rose" className="px-4 py-3 text-sm text-rose-700 mb-6">{error}</GlassCard>
      )}

      {/* KPI row */}
      {accounts.length > 0 && (
        <section className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          <KpiTile delay={80} icon={<LuServer className="w-4 h-4" />} label="Hosting accounts"
                   value={String(accounts.length)} sub={`${stats.active} active`} />
          <KpiTile delay={120} icon={<LuHardDrive className="w-4 h-4" />} label="Disk used"
                   value={fmtGb(stats.usedMb)} sub={stats.quotaGb ? `of ${stats.quotaGb} GB total` : undefined} />
          <KpiTile delay={160} icon={<LuShieldCheck className="w-4 h-4" />} label="SSL secured"
                   value={`${stats.ssl}/${accounts.length}`} sub="free certificates" />
          <KpiTile delay={200} icon={<LuGlobe className="w-4 h-4" />} label="Datacenter"
                   value="OVH" sub="NVMe storage" />
        </section>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2].map(i => (
            <GlassCard key={i} delay={80 + i * 40} className="p-5">
              <div className="h-5 w-2/3 rounded-md bg-slate-200/70 animate-pulse mb-3" />
              <div className="h-3 w-1/2 rounded-md bg-slate-200/70 animate-pulse mb-6" />
              <div className="h-2 w-full rounded-full bg-slate-200/70 animate-pulse" />
            </GlassCard>
          ))}
        </div>
      ) : accounts.length === 0 ? (
        <GlassCard delay={80} className="p-12 text-center max-w-xl mx-auto">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 text-amber-600 mb-4">
            <LuServer className="w-7 h-7" />
          </span>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No hosting accounts yet</h3>
          <p className="text-sm text-slate-600 mb-5 max-w-md mx-auto">
            Pick a plan to spin up a hosting account — domains, mail, databases and automatic backups included.
          </p>
          <Link
            to="/hosting"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition"
          >
            Browse hosting plans
            <LuExternalLink className="w-4 h-4" />
          </Link>
        </GlassCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {accounts.map((acct, idx) => {
            const pct = diskPct(acct);
            const pill = pillForStatus(acct.status);
            const hasSsl = !!acct.ssl_issued_at;
            return (
              <GlassCard key={acct.id} delay={240 + idx * 40} className="p-5 flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-violet-50 border border-violet-200 text-violet-700 flex-shrink-0">
                      <LuGlobe className="w-5 h-5" />
                    </span>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">
                        {acct.primary_domain || '(no domain assigned)'}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {acct.package_name || 'Hosting'} · since {new Date(acct.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                  </div>
                  <Pill label={pill.label} tone={pill.tone} />
                </div>

                {/* Disk meter */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                    <span className="inline-flex items-center gap-1.5"><LuHardDrive className="w-3.5 h-3.5" /> Disk</span>
                    <span className="tabular-nums">
                      {fmtGb(acct.current_disk_usage_mb)}{acct.disk_quota_gb ? ` / ${acct.disk_quota_gb} GB` : ''}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-200/80 overflow-hidden"
                       role="progressbar" aria-label="Disk usage"
                       aria-valuenow={pct == null ? undefined : Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
                    <div
                      className={`h-full rounded-full ${
                        pct == null ? 'bg-slate-300'
                          : pct > 90 ? 'bg-rose-500'
                          : pct > 75 ? 'bg-amber-400'
                          : 'bg-gradient-to-r from-[#feb44b] to-[#f39c12]'
                      }`}
                      style={{ width: `${pct ?? 4}%` }}
                    />
                  </div>
                </div>

                {/* DNS pointing notice — the account works, but the domain
                    won't reach it until its controller adds the records.
                    Full record details live on the account's Overview. */}
                {acct.status === 'active' && !acct.dns_linked_at && !acct.ssl_issued_at && (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800">
                    <span className="font-medium">Action needed:</span> your domain isn't pointed here yet —
                    website <em>and</em> email need DNS records added at your DNS provider.
                    <div className="mt-1.5 flex flex-wrap items-center gap-3">
                      <button
                        onClick={async () => {
                          setVerifyBusy(acct.id);
                          try {
                            const apiBase = process.env.REACT_APP_API_URL || '';
                            const token = localStorage.getItem('token');
                            const resp = await fetch(`${apiBase}/api/hosting/accounts/${acct.id}/dns/verify`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
                            const json = await resp.json();
                            setVerifyMsg(m => ({ ...m, [acct.id]: json.message || 'Checked' }));
                            if (json.success && json.data?.pointed) {
                              setAccounts(list => list.map(a => a.id === acct.id ? { ...a, dns_linked_at: new Date().toISOString() } : a));
                            }
                          } catch (e: any) {
                            setVerifyMsg(m => ({ ...m, [acct.id]: e.message || 'Network error' }));
                          } finally { setVerifyBusy(null); }
                        }}
                        disabled={verifyBusy === acct.id}
                        className="font-semibold bg-slate-900 text-white px-2.5 py-1 rounded-md hover:bg-slate-800 disabled:opacity-60"
                      >
                        {verifyBusy === acct.id ? 'Checking…' : 'Verify DNS'}
                      </button>
                      <button
                        onClick={() => setDnsGuideFor(acct.id)}
                        className="font-semibold text-amber-900 underline hover:text-amber-700"
                      >
                        View all records &amp; instructions
                      </button>
                    </div>
                    {verifyMsg[acct.id] && <div className="mt-1.5 text-[11px] text-amber-900">{verifyMsg[acct.id]}</div>}
                  </div>
                )}

                {/* Spec chips */}
                <div className="mt-4 flex flex-wrap gap-2">
                  <SpecChip icon={<LuCpu className="w-3.5 h-3.5" />} label={`${acct.cpu_quota ?? '—'} vCPU`} />
                  <SpecChip icon={<LuMemoryStick className="w-3.5 h-3.5" />} label={acct.ram_quota_mb ? fmtGb(acct.ram_quota_mb) + ' RAM' : '— RAM'} />
                  <SpecChip icon={<LuArrowDownUp className="w-3.5 h-3.5" />} label={acct.bandwidth_quota_gb ? `${acct.bandwidth_quota_gb} GB BW` : '— BW'} />
                  <SpecChip icon={<LuFileCode2 className="w-3.5 h-3.5" />} label={`PHP ${acct.php_version || '8.1'}`} />
                  <SpecChip
                    icon={<LuLock className="w-3.5 h-3.5" />}
                    label={hasSsl ? 'SSL' : 'SSL pending'}
                    tone={hasSsl ? 'emerald' : 'amber'}
                  />
                </div>

                {/* Footer */}
                <div className="mt-4 pt-3 border-t border-slate-200/60 flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-400 font-mono truncate">{acct.ip_address || acct.hostname || ''}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {acct.primary_domain && acct.status === 'active' && (
                      <a href={`https://${acct.primary_domain}`} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 px-2.5 py-1.5 rounded-lg hover:bg-slate-100/70 transition">
                        Visit <LuExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <Link to={`/customer/hosting/${acct.id}`}
                          className="text-xs bg-slate-900 text-white px-3.5 py-1.5 rounded-lg hover:bg-slate-800 font-medium transition">
                      Manage
                    </Link>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}

      {dnsGuideFor && (
        <DnsGuideModal hostingAccountId={dnsGuideFor} onClose={() => setDnsGuideFor(null)} />
      )}
    </DashShell>
  );
};

const KpiTile: React.FC<{ icon: React.ReactNode; label: string; value: string; sub?: string; delay?: number }> =
  ({ icon, label, value, sub, delay }) => (
    <GlassCard delay={delay} className="p-4">
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
        <span className="text-slate-400">{icon}</span>{label}
      </div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </GlassCard>
  );

const SpecChip: React.FC<{ icon: React.ReactNode; label: string; tone?: 'emerald' | 'amber' }> = ({ icon, label, tone }) => {
  const cls = tone === 'emerald'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : tone === 'amber'
    ? 'bg-amber-50 text-amber-800 border-amber-200'
    : 'bg-white/70 text-slate-600 border-slate-200';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border ${cls}`}>
      {icon}{label}
    </span>
  );
};

export default HostingDashboard;
